import { useEffect, useState, type ReactNode } from "react";
import { AlignLeft, ChevronDown, ChevronUp, Plus } from "lucide-react";
import { IconTextCell, RemoveButton, RowActionButton } from "./InterviewCells";
import { FullTextCell, FullTextPanel } from "./FullText";
import { LocationBoxPanel } from "./LocationBox";
import { usePanels } from "./useOnePanelOpen";

/**
 * s221 Lane 1, mechanism 2 — THE INTERVIEW ROW TABLE (SPEC: `Main.dc.html`, the rows under a tile picker).
 *
 * One row per thing the planner picked. The first column is the row's lead — the tile it came from
 * ("Child", "Dependant"), with the tile's mark — and the rest are the screen's cells. The actions sit
 * at the END of the row (rule 10: rows are added from the end of the row, never from a button under
 * the table):
 *
 *   Note · Add row · ×     — left-aligned, × directly after the last button (D-220.5, amended D-220.15)
 *
 * No Done button: the product saves as you go. Anything that opens under a row (the note, a contact
 * card, a writing box) is passed in as `panels` and rendered beneath it, full width.
 *
 * Presentational. The screen owns the rows and every write; this file writes nothing.
 * `gridTemplate` is the SPEC board's `cols` string, so two tables on one screen share column widths
 * (the children and dependants tables share "Who", s221 ch1 item 3).
 *
 * Look (s221 Lane 1b, D-221.28): no card around the table — header labels with the board's grey
 * sub-labels, a rule under the header and under each row, 44px controls, the lead's mark as a 24px badge.
 */

export type RowTableColumn = { key: string; label: string; sub?: string; strong?: boolean };

export type RowTableNote = {
  value: string;
  open: boolean;
  onToggle: () => void;
  /** s237 (D-237.30): called by the panel's Done with the value to store. Return the save, so Done waits for it and a
   *  refused save keeps the panel open with the draft (the long-answer box's rule, s228). */
  onCommit: (value: string) => void | Promise<unknown>;
};

export type RowTableRow = {
  rowId: string;
  /** The first column: the tile this row came from. */
  lead: string;
  /**
   * s223 (D-223.19) — the board's `typed` tile: the lead column is a BOX the planner types into, where a
   * tile's name would sit ("Someone else" → "my goddaughter's husband"). Present only on a typed tile's row;
   * every other row draws `lead` as words, exactly as before. The board's `full` is the placeholder.
   */
  leadInput?: { value: string; placeholder?: string; onCommit: (value: string) => void; testId?: string };
  /**
   * s226 (board v331, `noStanding`) — a CONTROL in the lead column, where the tile's name would sit: the executor row
   * that arrived with no standing asks for it on that row only. Drawn instead of `lead` and `leadMark`.
   */
  leadCell?: ReactNode;
  /** The tile's mark, drawn as the board's 24px badge beside the lead. */
  leadMark?: { mark: string; color: string };
  cells: ReactNode[];
  /** s233 (D-233.81, board v440 `isPin`): the pinned home row has no × — its slot stays, so every row's actions line up. */
  noRemove?: boolean;
  /** "Add row" appears only where a second row of the same kind makes sense (the SPEC's `noDup`). */
  canAddRow?: boolean;
  note?: RowTableNote;
  /** Anything that opens beneath the row — a contact card, a writing box. A folded row hides it (its second line). */
  panels?: ReactNode;
  /**
   * s233 (D-232.84 item 4) — what a FIRST-LINE cell opens (the address panel, a picked person's card, a typed "Other"):
   * drawn right under the first line, before the second line, and kept on a folded row so the first line stays editable.
   */
  lineOnePanels?: ReactNode;
  /**
   * s233 (D-232.77 item 6, D-232.87; board v417) — the row's fold, read only when the table has `foldRows`. `hasSecond`:
   * the row has a second line of fields to fold (only such a row gets the arrow). `filled`: any answer is in the row.
   */
  fold?: { hasSecond: boolean; filled: boolean; more?: number; toAnswer?: number };
  /**
   * s221 Lane 1d (G8): the row was removed THIS visit. It draws as ONE quiet line in its own place —
   * "Avery Barber removed · Undo" — so the planner never believes a person vanished. The board declares
   * no undo line; this uses its own quiet "ghost" link style (13px, the muted colour).
   */
  removed?: { label: string; onUndo: () => void };
};

/** s221 Lane 1d (G8): a table holding rows hidden on an EARLIER visit says so, once, above the table. */
export type HiddenRowsNotice = { count: number; noun: [string, string]; onRestore: () => void };

/**
 * s237 (D-237.30, board v462): the row NOTE opens the long-answer box — `LocationBoxPanel`, the same panel as every long
 * answer: the title "Note", a 108px writing space (`WRITING_PANEL`), Done (waits for the save), Cancel, "N / 600". The
 * board draws it as the `row.noteOn` panel (Main.dc.html:460–470). The old 64px note box and its rule-30 exemption
 * (D-223.11) are retired: one job, one piece (rule 159).
 */
export const NOTE_WORDS = { title: "Note", placeholder: "Add additional information" } as const;

/**
 * s226 (board v345, Reade: "ok agreed") — the cut name's words, as the board draws them: the bar under the row
 * ("Full name:", Main.dc.html `row.nameOpen`) and the hover title on the cut name (`row.nameTitle`). Held equal to
 * the board by a canary in `board-v334-look.test.tsx`.
 */
export const FULL_NAME = { label: "Full name:", title: "Show the full name" } as const;

/**
 * s227 — the name's type, as the board draws it (Main.dc.html, the name span inside `row.nameText`: font-size 14px,
 * #2B2A26, overflow-wrap: anywhere). Both name branches (the cut-aware one and the plain one) use it. Staging drew
 * 15px because the s226 canary checked the board's span against itself and never the product's class; the canary in
 * `board-v334-look.test.tsx` now reads the size (and, s227, the line-height) from the board and renders both branches.
 */
// s227 (D14): + the board's line-height 1.25 (the same span) — with the 3-line clamp now on every name column, the
// line-height is what sets a cut name's height (1.5 inherited drew it 3.5px taller per line, measured).
export const LEAD_NAME_CLASS = "text-[14px] leading-[1.25] text-warm-charcoal [overflow-wrap:anywhere]";

/**
 * s233 (D-233.16 point 3, D-233.35) — the fold arrow's slot at the row's LEFT, 24 x 24 as the board draws it (was 36px after
 * the ×, board v417). Every row of a folding table keeps the slot, arrow or not, so every row starts at the same x and the
 * × stays last. It is a grid track of its own, so the headers stay over their columns.
 */
export const FOLD_SLOT_PX = 24;
export const FOLD_TITLE = "Open or fold the rest of this row";
/**
 * s233 (rule 147, D-233.58 item 11): what the arrow opens, on hover — "Show 4 more questions — 3 to answer". Used when the
 * row says how many questions its second line holds (`fold.more`); otherwise the plain title.
 */
export function foldLabel(folded: boolean, more?: number, toAnswer?: number): string {
  if (!folded || more === undefined) return FOLD_TITLE;
  return `Show ${more} more question${more === 1 ? "" : "s"} — ${toAnswer ?? 0} to answer`;
}
/** s233 (rule 146): the row mark's column, after the arrow — 28px, the badge inside it (D-233.71 counts it). */
export const MARK_SLOT_PX = 28;
/** s233 (rule 151, D-233.60 item 5): the line between rows when a chapter turns `strongRowLine` on. */
export const ROW_LINE_STRONG = "#A89F8D";

/**
 * s233 (D-232.77 item 6, D-232.83 item 5, D-232.87) — which rows are folded. A row seen for the first time already filled
 * (opening the screen later) starts folded; a row started this visit starts open; Add row folds every filled row that has
 * a second line; the arrow opens or folds one row. Nothing folds on its own, so a row never folds while it is typed in.
 * s234 (FOLD-AUTO-OPEN-1, D-233.87): a folded row also OPENS on its own when the planner clicks or tabs into any box on
 * it (`open`) — never folds, and no other row changes.
 */
function useFoldedRows(rows: readonly RowTableRow[], on: boolean) {
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  const [seen, setSeen] = useState<ReadonlySet<string>>(() => new Set());
  const unseen = on ? rows.filter((r) => !r.removed && !seen.has(r.rowId)) : [];
  useEffect(() => {
    if (!unseen.length) return;
    setSeen((s) => new Set([...Array.from(s), ...unseen.map((r) => r.rowId)]));
    const first = unseen.filter((r) => r.fold?.filled && r.fold.hasSecond).map((r) => r.rowId);
    if (first.length) setFolded((f) => new Set([...Array.from(f), ...first]));
  }, [unseen.map((r) => r.rowId).join(",")]);
  return {
    isFolded: (rowId: string) => on && folded.has(rowId),
    toggle: (rowId: string) => setFolded((f) => { const n = new Set(f); if (n.has(rowId)) n.delete(rowId); else n.add(rowId); return n; }),
    open: (rowId: string) => setFolded((f) => { if (!f.has(rowId)) return f; const n = new Set(f); n.delete(rowId); return n; }),
    foldFilled: () => setFolded((f) => new Set([...Array.from(f), ...rows.filter((r) => r.fold?.filled && r.fold.hasSecond).map((r) => r.rowId)])),
  };
}

/** s233 (D-233.82): a row's side padding, in px — Tailwind `px-0.5` on the header and every row below. */
export const ROW_PAD_PX = 2;

/** The row table's gap, in px — Tailwind `gap-2` below. */
export const ROW_GAP_PX = 8;

/**
 * The narrowest the SPEC's `cols` can be: each fixed column's width, each `minmax(Npx, …)` column's
 * floor, plus the gaps between them. Pure. The table is at least this wide and otherwise fills its column,
 * so a flexible column never grows to its content's full width and pushes the table sideways
 * (found on staging, s221 Lane 1b: the day-to-day table took 1372px in a 1296px column).
 */
export function minTableWidth(gridTemplate: string, gapPx = ROW_GAP_PX): number {
  const tracks = gridTemplate.match(/minmax\([^)]*\)|\S+/g) ?? [];
  const floor = tracks.reduce((sum, t) => sum + Number((t.match(/(\d+(?:\.\d+)?)px/) ?? [])[1] ?? 0), 0);
  return floor + gapPx * Math.max(tracks.length - 1, 0);
}

export function InterviewRowTable({
  columns,
  rows,
  gridTemplate,
  rowsLead,
  rowsNote,
  onAddRow,
  onRemove,
  testIdPrefix = "interview-rows",
  hiddenNotice,
  cutLead = false,
  leadOneLine = false,
  foldRows = false,
  rowMarks = false,
  strongRowLine = false,
}: {
  /** Header labels, lead column first. The actions column needs no header. */
  columns: readonly RowTableColumn[];
  rows: readonly RowTableRow[];
  /** The SPEC `cols` string: one width per column plus one for the actions. */
  gridTemplate: string;
  rowsLead?: string;
  rowsNote?: string;
  onAddRow?: (rowId: string) => void;
  onRemove: (rowId: string) => void;
  testIdPrefix?: string;
  hiddenNotice?: HiddenRowsNotice;
  /**
   * s226 (board v345) — the name clamps at 3 lines and a CUT name opens its full text under the row (the shared
   * `FullTextCell`). Opt-in per table: Reade's addendum puts it on daily's name cell only in this commit; the other
   * tables keep the plain wrapping name until they are moved over.
   */
  cutLead?: boolean;
  /**
   * s233 (board 1 v443, D-233.75): with `cutLead`, the name keeps to ONE line (the board: "a name on one line beside its
   * mark", Day-to-Day) instead of three, so every row keeps one height (rule 133(a)); a cut name still opens in full.
   */
  leadOneLine?: boolean;
  /**
   * s233 (board v417) — rows with a second line fold (each row's `fold`). Opt-in per table: chapter two's tables use it;
   * chapter one's rows join by passing `foldRows` and each row's `fold` (Window 1 reuses this, it builds no second one).
   */
  foldRows?: boolean;
  /**
   * s233 (rule 146; D-233.70(7), D-233.76): every row starts with its tile's mark, in its own 28px column after the arrow —
   * also beside a picker in the lead column. Off by default (chapter one draws the mark inside its lead column).
   */
  rowMarks?: boolean;
  /** s233 (rule 151): the line between rows is #A89F8D. Off by default (the stone line). */
  strongRowLine?: boolean;
}) {
  const notice = hiddenNotice && hiddenNotice.count > 0 ? hiddenNotice : undefined;
  // s226 — the full-text panels under the rows join the screen's one-panel rule (rule 66).
  const panels = usePanels();
  const folds = useFoldedRows(rows, foldRows);
  /**
   * s234 (FOLD-AUTO-OPEN-1): focus or a press landing in a folded row opens it — except on the row's own controls: the
   * arrow (it toggles, so opening first would fold it straight back), Add row (it folds filled rows) and ×.
   */
  const ROW_CONTROLS = ["fold", "add", "remove"].map((k) => `${testIdPrefix}-${k}-`);
  const openOnEntry = (target: EventTarget, rowId: string) => {
    const control = target instanceof Element ? target.closest("[data-testid]")?.getAttribute("data-testid") ?? "" : "";
    if (ROW_CONTROLS.some((p) => control === `${p}${rowId}`)) return;
    folds.open(rowId);
  };
  if (rows.length === 0 && !notice) return null;
  const template = [foldRows ? `${FOLD_SLOT_PX}px` : "", rowMarks ? `${MARK_SLOT_PX}px` : "", gridTemplate].filter(Boolean).join(" ");
  const line = strongRowLine ? "border-[#A89F8D]" : "border-warm-stone";
  const grid = { gridTemplateColumns: template };
  // s233 (D-233.82): the floor includes the rows' side padding, so a table at its floor (wider than its box, scrolling
  // inside it) still holds every row's × — without it the tracks ran 2px past the row.
  const minWidth = minTableWidth(template) + 2 * ROW_PAD_PX;
  // s235 (D-235.56; D-235.25, rule 31): a table of FIXED columns hugs them — its frame (the header rule, every row line)
  // is exactly the columns wide, never the page. Only a table with a flexible (`fr`) column fills its width.
  const fixedColumns = !/\dfr\b/.test(template);

  return (
    <div className="mt-7 border-t border-warm-stone pt-6" data-testid={testIdPrefix}>
      {(rowsLead || rowsNote) && (
        <div className="mb-3 flex flex-col gap-0.5">
          {rowsLead && <span className="text-[15px] text-warm-charcoal">{rowsLead}</span>}
          {rowsNote && <span className="text-[13px] text-warm-muted">{rowsNote}</span>}
        </div>
      )}
      {notice && (
        <p className="mb-3 text-[13px] text-warm-muted" data-testid={`${testIdPrefix}-hidden-notice`}>
          {notice.count} {notice.count === 1 ? notice.noun[0] : notice.noun[1]} removed from this list earlier — still in your plan.{" "}
          <button type="button" onClick={notice.onRestore} className="text-warm-muted underline underline-offset-2 hover:text-warm-charcoal"
            data-testid={`${testIdPrefix}-hidden-restore`}>
            Bring {notice.count === 1 ? "it" : "them"} back
          </button>
        </p>
      )}
      {/* The table scrolls inside its own box only on a narrow screen; at the board's width it fits. */}
      <div className="overflow-x-auto">
        <div className={fixedColumns ? undefined : "w-full"} style={fixedColumns ? { width: minWidth } : { minWidth }}>
          <div className={`grid items-end gap-2 border-b ${line} px-0.5 pb-2 text-[13px]`} style={grid}>
            {foldRows && <div />}
            {rowMarks && <div />}
            {columns.map((c) => (
              <div key={c.key} className={c.strong ? "text-warm-charcoal" : "text-warm-muted"}>
                {c.label}
                {/* s226 (rule 124): 13px, the board's size; board v337: warm-muted (#4A4640 since s233) (#D9D1C1 is never text). */}
                {c.sub && <div className="text-[13px] text-warm-muted">{c.sub}</div>}
              </div>
            ))}
            <div />
          </div>

          {rows.map((r) => { const folded = folds.isFolded(r.rowId); return r.removed ? (
            <div key={r.rowId} className={`flex min-h-[44px] items-center gap-2 border-b ${line} px-0.5 py-2 text-[13px] text-warm-muted`}
              data-testid={`${testIdPrefix}-removed-${r.rowId}`}>
              <span>{r.removed.label} removed</span>
              <span aria-hidden="true">·</span>
              <button type="button" onClick={r.removed.onUndo} className="text-warm-muted underline underline-offset-2 hover:text-warm-charcoal"
                data-testid={`${testIdPrefix}-undo-${r.rowId}`}>
                Undo
              </button>
            </div>
          ) : (
            <div key={r.rowId} className={`border-b ${line}`} data-testid={`${testIdPrefix}-row-${r.rowId}`}
              onFocusCapture={folded ? (e) => openOnEntry(e.target, r.rowId) : undefined}
              onPointerDownCapture={folded ? (e) => openOnEntry(e.target, r.rowId) : undefined}>
              <div className="grid items-center gap-2 px-0.5 py-2" style={grid}>
                {/* s233 (D-233.35): the arrow's slot first, the same width on every row, so every row starts at the same x. */}
                {foldRows && (r.fold?.hasSecond || folded ? (
                  <button type="button" onClick={() => folds.toggle(r.rowId)} title={foldLabel(folded, r.fold?.more, r.fold?.toAnswer)} aria-label={foldLabel(folded, r.fold?.more, r.fold?.toAnswer)} aria-expanded={!folded}
                    className="flex shrink-0 items-center justify-center rounded-[6px] border border-warm-stone bg-warm-white text-sage-600 hover:border-sage-600 hover:bg-[#F1F5EF]"
                    style={{ width: FOLD_SLOT_PX, height: FOLD_SLOT_PX }}
                    data-testid={`${testIdPrefix}-fold-${r.rowId}`} data-folded={folded ? "true" : "false"}>
                    {folded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />}
                  </button>
                ) : (
                  <div className="shrink-0" style={{ width: FOLD_SLOT_PX, height: FOLD_SLOT_PX }} aria-hidden="true" data-testid={`${testIdPrefix}-fold-slot-${r.rowId}`} />
                ))}
                {rowMarks && (
                  <span className="flex h-6 w-7 shrink-0 items-center justify-center rounded-[6px] text-[8px] font-medium text-warm-white"
                    style={r.leadMark ? { backgroundColor: r.leadMark.color } : { visibility: "hidden" }} data-testid={`${testIdPrefix}-rowmark-${r.rowId}`}>
                    {r.leadMark?.mark}
                  </span>
                )}
                <div className="flex min-w-0 items-center gap-2">
                  {r.leadMark && !r.leadCell && !rowMarks && (
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-[8px] font-medium text-warm-white"
                      style={{ backgroundColor: r.leadMark.color }}
                      data-testid={`${testIdPrefix}-mark-${r.rowId}`}
                    >
                      {r.leadMark.mark}
                    </span>
                  )}
                  {r.leadCell ? (
                    <div className="min-w-0 flex-grow">{r.leadCell}</div>
                  ) : r.leadInput ? (
                    <IconTextCell
                      value={r.leadInput.value}
                      placeholder={r.leadInput.placeholder}
                      ariaLabel={r.leadInput.placeholder ?? r.lead}
                      onCommit={r.leadInput.onCommit}
                      testId={r.leadInput.testId}
                    />
                  ) : (
                    // s226 (board v345, Reade: "ok agreed"; Main.dc.html's name cell): the name wraps at most 3 lines
                    // (-webkit-line-clamp: 3) so the row keeps one height, and overflow-wrap: anywhere breaks ONE word
                    // wider than the cell. A CUT name is its own way to read it (rule 133, D-223.13): the shared FullText
                    // control makes it a button only when the build measures it clipped. No icon.
                    cutLead ? (
                      <FullTextCell text={r.lead} open={panels.isOpen(`${r.rowId}:fulltext`)} onToggle={() => panels.toggle(`${r.rowId}:fulltext`)}
                        textClassName={`${leadOneLine ? "line-clamp-1" : "line-clamp-3"} min-w-0 ${LEAD_NAME_CLASS}`} title={FULL_NAME.title}
                        testId={`${testIdPrefix}-lead-${r.rowId}`} />
                    ) : (
                      // Not moved over yet: the plain name, which wraps and breaks one long word (overflow-wrap: anywhere).
                      <span className={LEAD_NAME_CLASS} data-testid={`${testIdPrefix}-lead-${r.rowId}`}>{r.lead}</span>
                    )
                  )}
                </div>
                {r.cells.map((cell, i) => (
                  <div key={i} className="min-w-0">{cell}</div>
                ))}
                <div className="flex items-center gap-1" data-testid={`${testIdPrefix}-actions-${r.rowId}`}>
                  {r.note && (
                    <RowActionButton onClick={r.note.onToggle} title="Add additional information" ariaExpanded={r.note.open} testId={`${testIdPrefix}-note-${r.rowId}`}>
                      <AlignLeft className="h-3.5 w-3.5 text-warm-muted" aria-hidden="true" />
                      Note
                    </RowActionButton>
                  )}
                  {r.canAddRow && onAddRow && (
                    <RowActionButton onClick={() => { if (foldRows) folds.foldFilled(); onAddRow(r.rowId); }} strong testId={`${testIdPrefix}-add-${r.rowId}`}>
                      <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                      Add row
                    </RowActionButton>
                  )}
                  {r.noRemove ? <div className="h-11 w-8 shrink-0" aria-hidden="true" data-testid={`${testIdPrefix}-noremove-${r.rowId}`} /> : (
                  <RemoveButton onClick={() => onRemove(r.rowId)} label={`Remove ${r.lead}`} testId={`${testIdPrefix}-remove-${r.rowId}`} />)}
                </div>
              </div>

              {r.lineOnePanels}
              {cutLead && !r.leadInput && !r.leadCell && panels.isOpen(`${r.rowId}:fulltext`) && (
                <FullTextPanel label={FULL_NAME.label} text={r.lead} testId={`${testIdPrefix}-lead-${r.rowId}`} />
              )}
              {r.note?.open && (
                // s237 (D-237.30): the long-answer box — its own draft, Done saves and closes, Cancel closes.
                <LocationBoxPanel value={r.note.value} onCommit={r.note.onCommit} onClose={r.note.onToggle}
                  title={NOTE_WORDS.title} placeholder={NOTE_WORDS.placeholder} testId={`${testIdPrefix}-note-${r.rowId}`} />
              )}
              {!folded && r.panels}
            </div>
          ); })}
        </div>
      </div>
    </div>
  );
}
